import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, from, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { NIKO_API_URL } from '../tokens/api.tokens';
import { Tenant } from '../models/tenant.model';
import { decodeJwtPayload } from '../utils/decode-jwt';
import { AuthService } from './auth.service';

interface SelectTenantResponse {
  accessToken: string;
  tenant?: Tenant;
}

/**
 * Silently re-mints the tenant-scoped token (`niko_tenant_session`) when it
 * has expired, so the user's still-valid Cognito/Amplify session does not get
 * thrown away over a dead 8h-lived token (`UsersAuthModule`'s `expiresIn: '8h'`,
 * see BR-AUTH-07).
 *
 * Two constraints drive the design, both load-bearing:
 *
 * 1. It talks to `POST /auth/select-tenant` through `HttpBackend`, NOT
 *    `HttpClient`/`HttpClient`'s interceptor chain. Reasons:
 *      (a) `tokenInterceptor` is what CALLS this service on a 401 — going
 *          through `HttpClient` would send this request back into
 *          `tokenInterceptor`, which could 401 again and call back in here,
 *          recursing forever.
 *      (b) `AuthService.getAccessToken()` (what `tokenInterceptor` normally
 *          attaches) would resolve to the very EXPIRED scoped token this
 *          service exists to replace. The refresh call must instead carry the
 *          raw Amplify/Cognito access token, attached by hand below.
 *
 * 2. It never injects `HttpClient` or `AdminService`, preserving the
 *    documented rule (see `token.interceptor.ts`) that the interceptor chain
 *    never forms a DI cycle back into itself.
 */
@Injectable({ providedIn: 'root' })
export class ScopedTokenRefreshService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly base = inject(NIKO_API_URL);

  // `HttpBackend`-backed client: bypasses every registered `HttpInterceptorFn`,
  // including `tokenInterceptor` itself (see class JSDoc, reason 1a).
  private readonly http = new HttpClient(inject(HttpBackend));

  // Single-flight cache: every 401 that fires around the same time (a page
  // that issues several parallel requests) must share ONE refresh instead of
  // minting N scoped tokens. `refCount: false` keeps the result available to
  // late subscribers that call `refresh()` while the request is still in
  // flight; `finalize` below clears the cache once the shared request settles
  // (success or failure) so the NEXT 401, later, starts a fresh attempt.
  private inFlight$: Observable<string> | null = null;

  /**
   * Resolves to the new scoped token, or errors if the session cannot be
   * recovered — in which case the user HAS been signed out and redirected to
   * `/login` first. That guarantee is unconditional (BR-AUTH-07): every
   * failure mode, including the ones detected before any HTTP call goes out,
   * funnels through `abandonSession` below. Composing the whole thing inside
   * the single-flight pipe is what makes that true — an early `throwError`
   * returned straight out of this method would skip the recovery path and
   * strand the user on the dead token this service exists to replace.
   */
  refresh(): Observable<string> {
    if (this.inFlight$) return this.inFlight$;

    this.inFlight$ = this.mintScopedToken().pipe(
      catchError((err: unknown) => this.abandonSession(err)),
      finalize(() => {
        this.inFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlight$;
  }

  /** The happy path: ask the backend for a fresh scoped token and store it. */
  private mintScopedToken(): Observable<string> {
    const tenantId = this.currentTenantIdClaim();
    if (!tenantId) {
      // The stored token is present but unreadable (corrupted write, manual
      // tampering, a foreign token shape). There is no tenant to ask for, so
      // this is unrecoverable exactly like a rejected refresh — and it is
      // handled as one by the caller's `catchError`.
      return throwError(() => new Error('SCOPED_TOKEN_REFRESH_NO_TENANT_CLAIM'));
    }

    return from(this.authService.getCognitoAccessToken()).pipe(
      switchMap((cognitoToken) => {
        if (!cognitoToken) return throwError(() => new Error('SCOPED_TOKEN_REFRESH_NO_COGNITO_SESSION'));
        return this.http.post<SelectTenantResponse>(
          `${this.base}/auth/select-tenant`,
          { tenantId },
          { headers: { Authorization: `Bearer ${cognitoToken}` } },
        );
      }),
      tap(({ accessToken, tenant }) => {
        this.authService.setScopedToken(accessToken);
        if (tenant?.name) this.authService.setScopedTenantName(tenant.name);
      }),
      map(({ accessToken }) => accessToken),
    );
  }

  /**
   * Unrecoverable session: log out and send the user back to `/login` rather
   * than leaving them stuck with a dead token forever, then re-throw the
   * original error so the caller still sees why.
   *
   * The `signOut()` rejection is deliberately swallowed. `clearScopedToken()`
   * runs synchronously inside `signOut()` BEFORE anything that can reject
   * (Amplify's own network call), so by the time a rejection surfaces the
   * credentials are already gone — letting it propagate would skip the
   * navigation and strand the user with no token and no route out, which is
   * the very failure this method exists to prevent.
   */
  private abandonSession(err: unknown): Observable<never> {
    return from(this.authService.signOut()).pipe(
      catchError(() => of(void 0)),
      switchMap(() => {
        this.router.navigate(['/login']);
        return throwError(() => err as HttpErrorResponse | Error);
      }),
    );
  }

  /**
   * Reads `selectedTenantId` off the CURRENT (expired) scoped token. The
   * backend re-verifies the tenant independently on the refresh call, so
   * trusting this claim only to pick WHICH tenant to ask for is safe — see
   * `decodeJwtPayload`.
   */
  private currentTenantIdClaim(): string | null {
    const scoped = this.authService.getScopedToken();
    if (!scoped) return null;
    const claims = decodeJwtPayload(scoped);
    const tenantId = claims?.['selectedTenantId'];
    return typeof tenantId === 'string' && tenantId.length > 0 ? tenantId : null;
  }
}
